import { useLayoutEffect, useRef } from 'react'
import { Plus } from 'lucide-react'
import { ActionDependant, View, useEvaluatedAttributes } from '@ea-lab/reactive-json'
import { useRowContext } from '../editable/useRowContext'
import { resolveRowAction } from '../editable/rowPaths'

/** Plain text carried by an rjBuild fragment (`' Ajouter un lien'`, a list…). */
const textOf = (content) => {
  const text = (Array.isArray(content) ? content : [content])
    .filter((part) => typeof part === 'string')
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text === '' ? null : text
}

/**
 * ReactiveJSON primitive — a single list-row action button, handled inline by
 * the Page Builder (no modal): `add`, `insert`, `remove`, `up`, `down`.
 *
 * Typical use: the "add a row" button placed right after the Switch, outside
 * any row (the per-row up/down/remove trio is `BuilderRowToolbar`):
 *
 *   - type: BuilderRowAction
 *     field: stats                       # dotted for a nested list: regions.destinations
 *     action: add
 *     defaultItem: { value: '0', label: 'Nouvelle stat' }
 *     attributes: { class: 'builder-row-add', title: 'Ajouter une stat' }
 *     content: [{ type: LucideIcon, name: Plus, size: 12 }, ' Ajouter une stat']
 *
 * An `add` DOES NOT render that content: it shows a bare « + » in the visual
 * language of the row rail (white, round, a hairline border and a soft shadow),
 * itself revealed only on hover of the list — and its legend (« Ajouter »)
 * unfolds only once the pointer, or the focus, is on the « + ». Exactly how the
 * rail shows nothing but its grip until it is reached. What the def wrote —
 * its `title:`, or the text of its `content:` — becomes the button's ACCESSIBLE
 * NAME (« Ajouter un lien »), which is what a screen reader announces and what
 * makes a page of identical buttons distinguishable. The legend stays in the
 * DOM at all times (only its width is animated), so that name never depends on
 * a hover state; WCAG 2.5.3 holds because the visible label is a prefix of it.
 *
 * Emits `data-builder-row-action` + `data-builder-field` + `data-builder-row-path`
 * (the TARGET ARRAY, relative to the field) + `data-builder-row-index`;
 * `BuilderBlockWrapper` applies the action to the array and commits it whole.
 * Prefer non-empty placeholder values in `defaultItem`: an empty cell is
 * still clickable (it shows its placeholder in edit mode) but a visible
 * value is easier to find.
 */
export const BuilderRowAction = ({ props, path, datafield, currentData }) => {
  const { topField, nested, rowPath } = useRowContext(props, path, datafield)
  const target = resolveRowAction({ action: props.action, nested, rowPath })
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})
  const { className, ...restAttrs } = evaluatedAttrs
  const bandRef = useRef(null)
  // `add` is the only action rendered as a band; the others (a stray `insert`
  // or `remove` left in a def) keep the bare button they always were.
  const isAdd = props.action === 'add'
  const classes = [isAdd ? null : 'builder-edit-only', className].filter(Boolean).join(' ')

  // Which element's hover reveals the band. The def almost always wraps the
  // button in a `builder-edit-only` div of its own, and that wrapper is exactly
  // as tall as the band — hovering it would mean hovering something invisible.
  // So a wrapper that holds nothing else hands the role to ITS parent: the
  // container that holds the list, which is what the user is actually pointing
  // at when they want one more row.
  useLayoutEffect(() => {
    const band = bandRef.current
    if (!band) return
    const wrapper = band.parentElement
    if (!wrapper) return
    const host = wrapper.childElementCount === 1 ? (wrapper.parentElement ?? wrapper) : wrapper
    host.setAttribute('data-builder-add-host', '')
    return () => host.removeAttribute('data-builder-add-host')
  })

  // What the def wrote is the ACCESSIBLE name, not the visible one: its
  // `title:`, or failing that the text of its `content:` (« ␣Ajouter un lien »
  // beside a Plus icon), trimmed of the icon it no longer renders.
  const accessibleName = restAttrs.title ?? textOf(props.content) ?? 'Ajouter une entrée'

  const button = (
    <button
      type="button"
      {...restAttrs}
      title={isAdd ? accessibleName : restAttrs.title}
      aria-label={isAdd ? accessibleName : undefined}
      className={classes}
      disabled={target == null}
      data-builder-row-action={target ? props.action : undefined}
      data-builder-field={target ? topField : undefined}
      data-builder-row-path={target?.arrayPath ?? undefined}
      data-builder-row-index={target?.index ?? undefined}
      data-builder-options={props.defaultItem != null ? JSON.stringify({ defaultItem: props.defaultItem }) : undefined}
    >
      {isAdd ? (
        <>
          <Plus size={13} aria-hidden="true" />
          {/* La légende ne se déplie qu'au survol du « + » lui-même (CSS) : le
              nœud existe toujours, donc le nom accessible ne dépend pas d'un
              état de survol. */}
          <span className="builder-row-add-label">Ajouter</span>
        </>
      ) : props.content != null && (
        <View
          props={props.content}
          path={path + '.content'}
          datafield="content"
          currentData={currentData?.content}
        />
      )}
    </button>
  )

  if (!isAdd) return <ActionDependant {...props}>{button}</ActionDependant>

  return (
    <ActionDependant {...props}>
      <span ref={bandRef} className="builder-edit-only builder-row-add-band">
        {button}
      </span>
    </ActionDependant>
  )
}
